/**
 * Aproxima o mapa de um imóvel do CAR escolhido na ficha do foco (clique na
 * linha dele). Não precisa caber inteiro: o imóvel ocupa uns 40% da parte
 * livre do mapa — a que sobra fora dos painéis —, o bastante para ver a área
 * e o que há em volta, com lugar para a ficha ao lado. Só aproxima (quem já está mais perto
 * fica no seu zoom, o mapa só centra) e não passa de um ponto em que o
 * círculo de 375 m cobriria a tela toda.
 */
import type OlMap from 'ol/Map';
import { getCenter, getHeight, getWidth, type Extent } from 'ol/extent';
import { lonLatToMap } from '../map/projection';
import { panelPadding } from '../map/viewControl';
import type { Caixa } from '../map/popupArea';

/** Fração do lado menor da parte livre que o lado maior do imóvel ocupa (sobra lugar para a ficha ao lado). */
const FRACAO = 0.4;
/** Mais perto que isto, o círculo de 375 m (750 m) passaria de ~600 px. */
const PX_DO_CIRCULO_NO_MAXIMO = 600;
/** Uma faixa ao lado da ficha só serve se tiver ao menos isto no lado menor. */
const FAIXA_MINIMA_PX = 160;
const DURACAO_MS = 650;

/**
 * A maior faixa de `livre` fora de `ocupada` (acima, abaixo, à esquerda ou à
 * direita dela); `livre` inteira se `ocupada` não a toca ou se nenhuma faixa
 * tem `FAIXA_MINIMA_PX` no lado menor.
 */
export function faixaLivre(livre: Caixa, ocupada: Caixa | null): Caixa {
  if (!ocupada || ocupada.right <= livre.left || ocupada.left >= livre.right || ocupada.bottom <= livre.top || ocupada.top >= livre.bottom)
    return livre;
  const faixas: Caixa[] = [
    { ...livre, bottom: Math.min(livre.bottom, ocupada.top) },
    { ...livre, top: Math.max(livre.top, ocupada.bottom) },
    { ...livre, right: Math.min(livre.right, ocupada.left) },
    { ...livre, left: Math.max(livre.left, ocupada.right) },
  ];
  const lado = (c: Caixa) => Math.min(c.right - c.left, c.bottom - c.top);
  const area = (c: Caixa) => Math.max(0, c.right - c.left) * Math.max(0, c.bottom - c.top);
  const boas = faixas.filter((c) => lado(c) >= FAIXA_MINIMA_PX).sort((a, b) => area(b) - area(a));
  return boas[0] ?? livre;
}

/**
 * Centro e resolução para o imóvel (`extensao`, na projeção do mapa) no meio
 * de `faixa` (px, relativa ao mapa de `tamanho` px). `resolucaoMinima`: o
 * mais perto permitido; `resolucaoAtual`: o mapa nunca se afasta dela.
 */
export function vistaDoImovel(
  extensao: Extent,
  faixa: Caixa,
  tamanho: [number, number],
  resolucaoAtual: number,
  resolucaoMinima: number,
): { center: [number, number]; resolution: number } {
  const ladoPx = Math.max(1, Math.min(faixa.right - faixa.left, faixa.bottom - faixa.top) * FRACAO);
  const maior = Math.max(getWidth(extensao), getHeight(extensao));
  const resolution = Math.min(resolucaoAtual, Math.max(resolucaoMinima, maior / ladoPx));
  const [cx, cy] = getCenter(extensao) as [number, number];
  // O centro do imóvel cai no meio da faixa: o centro do mapa fica deslocado do meio da tela.
  const dx = (faixa.left + faixa.right) / 2 - tamanho[0] / 2;
  const dy = (faixa.top + faixa.bottom) / 2 - tamanho[1] / 2;
  return { center: [cx - dx * resolution, cy + dy * resolution], resolution };
}

/** Unidades do mapa por metro em volta do ponto (a projeção do GOES não é métrica no chão). */
function unidadesPorMetro(lat: number, lon: number): number {
  const dlat = 1000 / 111_320;
  const [x0, y0] = lonLatToMap(lon, lat - dlat / 2);
  const [x1, y1] = lonLatToMap(lon, lat + dlat / 2);
  return Math.hypot(x1! - x0!, y1! - y0!) / 1000;
}

/**
 * Aproxima `map` de `extensao`, fora dos painéis sobre o mapa; no desktop a
 * ficha sai sozinha de cima do imóvel (`evitarTambemOImovel`). `cobertos`:
 * mais o que conta para o tamanho da parte livre. `focoParado` (celular, onde
 * a ficha não desvia e é quase da largura da tela): o zoom é feito em volta
 * do foco, que não sai do lugar, e o imóvel cresce na faixa livre ao lado da
 * ficha — não precisa caber inteiro.
 */
export function aproximarDoImovel(
  map: OlMap,
  extensao: Extent,
  lat: number,
  lon: number,
  cobertos: readonly HTMLElement[] = [],
  opcoes: { focoParado?: boolean } = {},
): void {
  const view = map.getView();
  const tamanho = map.getSize();
  const atual = view.getResolution();
  if (!tamanho || !atual) return;
  const [cima, direita, baixo, esquerda] = panelPadding();
  const livre: Caixa = { left: esquerda, top: cima, right: tamanho[0] - direita, bottom: tamanho[1] - baixo };
  // O que cobre o mapa, em px relativos a ele: os painéis que o app põe sobre o mapa (irmãos
  // dele no <main>: a lista dos focos, a legenda), como em PainelCar, e `cobertos`.
  const vp = map.getViewport().getBoundingClientRect();
  const alvo = map.getTargetElement();
  const elementos = [...(alvo?.parentElement?.children ?? [])].filter((el) => el !== alvo && !el.hasAttribute('data-moldura-previsao'));
  let faixa = livre;
  for (const el of [...elementos, ...cobertos]) {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    faixa = faixaLivre(faixa, { left: r.left - vp.left, top: r.top - vp.top, right: r.right - vp.left, bottom: r.bottom - vp.top });
  }
  const minima = Math.max(view.getMinResolution(), (750 * unidadesPorMetro(lat, lon)) / PX_DO_CIRCULO_NO_MAXIMO);
  const vista = vistaDoImovel(extensao, faixa, tamanho as [number, number], atual, minima);
  view.cancelAnimations();
  if (opcoes.focoParado) {
    // O foco fica no mesmo pixel, e com ele a ficha presa a ele: só o zoom muda.
    view.animate({ resolution: vista.resolution, anchor: lonLatToMap(lon, lat) as [number, number], duration: DURACAO_MS });
    return;
  }
  view.animate({ center: vista.center, resolution: vista.resolution, duration: DURACAO_MS });
}
